import React, { useEffect, useMemo, useState } from 'react'
import { Accordion, Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { groupService } from '../../services/group.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { userService } from '../../services/user.service'
import { Group, User } from '../../types'
import { type DocumentOwnerDisplayFields, formatOwnerLabel } from '../../utils/documentOwnerDisplay'
import { InputPermissionsGroup } from './InputPermissionsGroup'
import { InputPermissionsUser } from './InputPermissionsUser'
import { InputSelect } from './InputSelect'
import './PermissionsForm.scss'

export interface PermissionsFormValue {
  owner?: number | null
  set_permissions?: {
    view?: {
      users?: number[]
      groups?: number[]
    }
    change?: {
      users?: number[]
      groups?: number[]
    }
  }
}

interface PermissionsFormProps {
  value: PermissionsFormValue
  onChange: (value: PermissionsFormValue) => void
  accordion?: boolean
  users?: User[]
  groups?: Group[]
  permissionUsersFallback?: Array<{ id: number; username: string }>
  permissionGroupsFallback?: Array<{ id: number; name: string }>
  /** When the viewer cannot list users, seed the owner dropdown label for the current owner id */
  ownerDisplayFallback?: DocumentOwnerDisplayFields | null
  showTitle?: boolean
  disabled?: boolean
}

export const PermissionsForm: React.FC<PermissionsFormProps> = ({
  value,
  onChange,
  accordion = false,
  users: providedUsers,
  groups: providedGroups,
  permissionUsersFallback = [],
  permissionGroupsFallback = [],
  ownerDisplayFallback = null,
  showTitle = true,
  disabled = false,
}) => {
  const { t } = useTranslation()
  const [users, setUsers] = useState<User[]>([])
  const [groups, setGroups] = useState<Group[]>([])

  useEffect(() => {
    loadUsers().catch(() => {}) // Safety net - prevent unhandled rejection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedUsers])

  useEffect(() => {
    loadGroups().catch(() => {}) // Safety net - prevent unhandled rejection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedGroups])

  const loadUsers = async () => {
    try {
      // Only load users if not provided via props
      if (!providedUsers) {
        if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
          setUsers([])
          return
        }
        const usersResponse = await userService.listAll({ page: 1, page_size: 100000, full_perms: true })
        setUsers(usersResponse.results)
      } else {
        setUsers(providedUsers)
      }
    } catch (error) {}
  }

  const loadGroups = async () => {
    try {
      if (!providedGroups) {
        if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.Group)) {
          setGroups([])
          return
        }
        const groupsResponse = await groupService.listAll({ page: 1, page_size: 100000, full_perms: true })
        setGroups(groupsResponse.results)
      } else {
        setGroups(providedGroups)
      }
    } catch (error) {}
  }

  const ownerSelectItems = useMemo(() => {
    const list = [...users]
    const oid = value.owner
    if (oid == null || !ownerDisplayFallback || ownerDisplayFallback.id !== oid) {
      return list
    }
    if (list.some((u) => u.id === oid)) {
      return list
    }
    const label = formatOwnerLabel(ownerDisplayFallback)
    list.unshift({
      id: ownerDisplayFallback.id,
      username: label,
      first_name: ownerDisplayFallback.first_name ?? '',
      last_name: ownerDisplayFallback.last_name ?? '',
    } as User)
    return list
  }, [users, value.owner, ownerDisplayFallback])

  const permissionUsersSelectItems = useMemo(() => {
    const list = [...users]
    const existingIds = new Set(list.map((u) => u.id))
    permissionUsersFallback.forEach((user) => {
      if (!existingIds.has(user.id)) {
        list.push({
          id: user.id,
          username: user.username,
          first_name: '',
          last_name: '',
        } as User)
      }
    })
    return list
  }, [users, permissionUsersFallback])

  const permissionGroupsSelectItems = useMemo(() => {
    const list = [...groups]
    const existingIds = new Set(list.map((g) => g.id))
    permissionGroupsFallback.forEach((group) => {
      if (!existingIds.has(group.id)) {
        list.push({
          id: group.id,
          name: group.name,
          permissions: [],
          user_set: [],
        } as Group)
      }
    })
    return list
  }, [groups, permissionGroupsFallback])

  const handleOwnerChange = (ownerId: number | null) => {
    onChange({
      ...value,
      owner: ownerId,
    })
  }

  const handlePermissionChange = (type: 'view' | 'change', field: 'users' | 'groups', selectedIds: number[]) => {
    onChange({
      ...value,
      set_permissions: {
        ...value.set_permissions,
        [type]: {
          ...(value.set_permissions?.[type] || {}),
          [field]: selectedIds,
        },
      },
    })
  }

  const renderForm = () => (
    <div>
      {/* Owner */}
      <div className="row">
        <div className="col-lg-3">
          <Form.Label className="d-block my-2">{t('owner')}:</Form.Label>
        </div>
        <div className="col-lg-9">
          <InputSelect
            value={value.owner || null}
            onChange={handleOwnerChange}
            items={ownerSelectItems}
            bindLabel="username"
            allowNull={true}
            multiple={false}
            placeholder={t('no_owner')}
            disabled={disabled}
          />
        </div>
      </div>
      <small className="form-text text-muted text-end d-block mt-n2">{t('objects_without_owner_can_be_viewed_by_all')}</small>

      {/* View Permissions */}
      <h6 className="mt-3">{t('view')}</h6>
      <div className="mb-2">
        <div className="row mb-1">
          <div className="col-lg-3">
            <Form.Label className="d-block my-2">{t('users')}:</Form.Label>
          </div>
          <div className="col-lg-9">
            <InputPermissionsUser
              value={value.set_permissions?.view?.users || []}
              users={permissionUsersSelectItems}
              onChange={(users) => handlePermissionChange('view', 'users', users)}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3">
            <Form.Label className="d-block my-2">{t('groups')}:</Form.Label>
          </div>
          <div className="col-lg-9">
            <InputPermissionsGroup
              value={value.set_permissions?.view?.groups || []}
              groups={permissionGroupsSelectItems}
              onChange={(groups) => handlePermissionChange('view', 'groups', groups)}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Edit Permissions */}
      <h6 className="mt-4">{t('edit')}</h6>
      <div>
        <div className="row mb-1">
          <div className="col-lg-3">
            <Form.Label className="d-block my-2">{t('users')}:</Form.Label>
          </div>
          <div className="col-lg-9">
            <InputPermissionsUser
              value={value.set_permissions?.change?.users || []}
              users={permissionUsersSelectItems}
              onChange={(users) => handlePermissionChange('change', 'users', users)}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3">
            <Form.Label className="d-block my-2">{t('groups')}:</Form.Label>
          </div>
          <div className="col-lg-9">
            <InputPermissionsGroup
              value={value.set_permissions?.change?.groups || []}
              groups={permissionGroupsSelectItems}
              onChange={(groups) => handlePermissionChange('change', 'groups', groups)}
              disabled={disabled}
            />
          </div>
        </div>
        <small className="form-text text-muted text-end d-block">{t('edit_permissions_also_grant_view_permissions')}</small>
      </div>
    </div>
  )

  if (accordion) {
    return (
      <Accordion defaultActiveKey="">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{t('edit_permissions')}</Accordion.Header>
          <Accordion.Body>{renderForm()}</Accordion.Body>
        </Accordion.Item>
      </Accordion>
    )
  }

  return (
    <>
      {showTitle && <h5>{t('permissions')}</h5>}
      {renderForm()}
    </>
  )
}

export default PermissionsForm
